/* ==========================================================================
   TYLPS — Page produit
   Galerie (carrousel, miniatures, zoom au survol, aperçu de couleur),
   sélecteur de variantes, stock, estimation de livraison, barre d'achat collante.
   ========================================================================== */
(() => {
  'use strict';

  const T = window.TYLPS || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
  const money = (cents) => (T.formatMoney ? T.formatMoney(cents) : (cents / 100).toFixed(2));

  /* ==========================================================================
     Galerie
     ========================================================================== */
  class TylpsGallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.track = $('[data-gallery-track]', this);
      this.thumbsWrap = $('[data-gallery-thumbs]', this);
      this.preview = $('[data-gallery-preview]', this);
      if (!this.track) return;

      $$('[data-thumb]', this).forEach((thumb) => thumb.addEventListener('click', () => this.show(thumb.dataset.thumb)));
      $('[data-gallery-prev]', this)?.addEventListener('click', () => this.step(-1));
      $('[data-gallery-next]', this)?.addEventListener('click', () => this.step(1));

      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.syncFromScroll());
      }, { passive: true });

      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.step(1);
        if (e.key === 'ArrowLeft') this.step(-1);
      });

      if (this.dataset.zoom === 'true') this.initZoom();

      // Position initiale sur le média de la variante sélectionnée
      const active = $('.tylps-gallery__slide.is-active', this.track);
      if (active && active !== this.track.firstElementChild) {
        if (this.isGrid()) this.track.prepend(active);
        else this.track.scrollLeft = active.offsetLeft;
      }
    }

    isGrid() {
      return this.classList.contains('tylps-gallery--grid') && isDesktop();
    }

    get slides() {
      return Array.from(this.track.children);
    }

    currentIndex() {
      const w = this.track.clientWidth || 1;
      return Math.round(this.track.scrollLeft / w);
    }

    step(dir) {
      const slides = this.slides;
      let i = this.currentIndex() + dir;
      if (i < 0) i = slides.length - 1;
      if (i >= slides.length) i = 0;
      this.show(slides[i].dataset.mediaId);
    }

    show(mediaId) {
      const slide = this.track.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide) return;
      this.hidePreview();
      if (this.isGrid()) {
        if (slide !== this.track.firstElementChild) {
          this.track.prepend(slide);
          if (!reduceMotion) slide.animate([{ opacity: 0, transform: 'scale(0.98)' }, { opacity: 1, transform: 'none' }], { duration: 550, easing: 'cubic-bezier(0.22, 0.75, 0.15, 1)' });
        }
        const top = this.getBoundingClientRect().top;
        if (top < 0) window.scrollBy({ top: top - 100, behavior: reduceMotion ? 'auto' : 'smooth' });
      } else {
        this.track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      this.setActive(mediaId);
    }

    syncFromScroll() {
      if (this.isGrid()) return;
      const slide = this.slides[this.currentIndex()];
      if (slide) this.setActive(slide.dataset.mediaId);
    }

    setActive(mediaId) {
      this.slides.forEach((s) => {
        const active = s.dataset.mediaId === String(mediaId);
        s.classList.toggle('is-active', active);
        const video = s.querySelector('video');
        if (video) active ? video.play().catch(() => {}) : video.pause();
      });
      $$('[data-thumb]', this).forEach((t) => {
        const active = t.dataset.thumb === String(mediaId);
        t.classList.toggle('is-active', active);
        if (active && this.thumbsWrap) {
          const wrap = this.thumbsWrap;
          const vertical = wrap.scrollHeight > wrap.clientHeight + 4 && getComputedStyle(wrap).flexDirection === 'column';
          if (vertical) wrap.scrollTo({ top: t.offsetTop - wrap.clientHeight / 2 + t.clientHeight / 2, behavior: 'smooth' });
          else wrap.scrollTo({ left: t.offsetLeft - wrap.clientWidth / 2 + t.clientWidth / 2, behavior: 'smooth' });
        }
      });
      $$('[data-dot]', this).forEach((d) => d.classList.toggle('is-active', d.dataset.dot === String(mediaId)));
    }

    showPreview(src) {
      if (!src || !this.preview) return;
      if (this.preview.getAttribute('src') !== src) this.preview.src = src;
      this.preview.hidden = false;
      requestAnimationFrame(() => this.preview.classList.add('is-visible'));
    }

    hidePreview() {
      if (!this.preview) return;
      this.preview.classList.remove('is-visible');
    }

    initZoom() {
      $$('[data-zoom-target].tylps-zoomable', this).forEach((target) => {
        const img = target.querySelector('img');
        if (!img) return;
        target.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse' || !canHover()) return;
          target.classList.add('is-zooming');
        });
        target.addEventListener('pointermove', (e) => {
          if (!target.classList.contains('is-zooming')) return;
          const r = target.getBoundingClientRect();
          const x = ((e.clientX - r.left) / r.width) * 100;
          const y = ((e.clientY - r.top) / r.height) * 100;
          img.style.transformOrigin = `${x}% ${y}%`;
        });
        target.addEventListener('pointerleave', () => target.classList.remove('is-zooming'));
      });
    }
  }
  if (!customElements.get('tylps-gallery')) customElements.define('tylps-gallery', TylpsGallery);

  /* ==========================================================================
     Produit : variantes, prix, stock, livraison, barre collante
     ========================================================================== */
  class TylpsProduct extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try {
        this.product = JSON.parse($('[data-product-json]', this).textContent);
      } catch (e) {
        return;
      }
      if (!Array.isArray(this.product?.variants)) {
        // Données incomplètes (ex. produit choisi dans une section) : on recharge le produit
        this._ready = false;
        const url = this.dataset.url || this.product?.url;
        if (url && !this._loading) {
          this._loading = true;
          fetch(`${url}.js`)
            .then((r) => r.json())
            .then((p) => {
              $('[data-product-json]', this).textContent = JSON.stringify(p);
              this._loading = false;
              this.connectedCallback();
              document.dispatchEvent(new CustomEvent('tylps:product:ready', { detail: { el: this } }));
            })
            .catch(() => (this._loading = false));
        }
        return;
      }
      try {
        this.inventory = JSON.parse($('[data-inventory-json]', this)?.textContent || '{}');
      } catch (e) {
        this.inventory = {};
      }
      this.form = document.getElementById(this.dataset.formId);
      this.idInput = this.form?.querySelector('[data-variant-id]');
      this.gallery = $('tylps-gallery', this);
      const currentId = parseInt(this.idInput?.value, 10);
      this.variant = this.product.variants.find((v) => v.id === currentId) || this.product.variants[0];

      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-variant-picker]')) this.onOptionChange();
      });

      // Aperçu de la couleur au survol d'une pastille
      $$('[data-variant-picker] label[data-preview]', this).forEach((label) => {
        label.addEventListener('mouseenter', () => {
          if (!canHover()) return;
          const legend = label.closest('[data-option-group]')?.querySelector('[data-option-label]');
          if (legend) legend.textContent = label.dataset.value;
          if (label.dataset.preview) this.gallery?.showPreview(label.dataset.preview);
        });
        label.addEventListener('mouseleave', () => {
          this.gallery?.hidePreview();
          this.updateLabels(this.getOptions());
        });
      });

      this.initDelivery();
      this.initSticky();
      this.updateAvailability(this.getOptions());
    }

    disconnectedCallback() {
      clearInterval(this._deliveryTimer);
      this._stickyObserver?.disconnect();
    }

    getOptions() {
      return $$('[data-option-group]', this).map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        return group.querySelector('input:checked')?.value ?? null;
      });
    }

    findVariant(options) {
      return this.product.variants.find((v) => v.options.every((o, i) => o === options[i]));
    }

    onOptionChange() {
      const options = this.getOptions();
      this.updateLabels(options);
      this.updateAvailability(options);
      const variant = this.findVariant(options);
      this.setVariant(variant);
    }

    updateLabels(options) {
      $$('[data-option-group]', this).forEach((group, i) => {
        const label = group.querySelector('[data-option-label]');
        if (label && options[i] != null) label.textContent = options[i];
      });
    }

    updateAvailability(options) {
      $$('[data-option-group]', this).forEach((group, i) => {
        $$('input[type="radio"]', group).forEach((input) => {
          const candidate = options.slice();
          candidate[i] = input.value;
          const match = this.product.variants.filter((v) => v.options.every((o, j) => j === i ? o === input.value : o === candidate[j]));
          const available = match.some((v) => v.available);
          const label = group.querySelector(`label[for="${input.id}"]`);
          label?.classList.toggle('is-unavailable', !available);
        });
      });
    }

    setVariant(variant) {
      this.variant = variant;
      const addBtn = this.form?.querySelector('[data-add-button]');
      const stickyBtn = $('[data-sticky-button]', this);
      const payment = this.form?.querySelector('[data-payment-button]');

      if (!variant) {
        this.setButton(addBtn, false, T.strings?.unavailable || 'Indisponible');
        if (stickyBtn) stickyBtn.disabled = true;
        if (payment) payment.hidden = true;
        return;
      }

      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Prix
      $$('[data-price]', this).forEach((el) => (el.textContent = money(variant.price)));
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      $$('[data-compare-price]', this).forEach((el) => {
        el.hidden = !onSale;
        el.textContent = onSale ? money(variant.compare_at_price) : '';
      });
      $$('[data-savings]', this).forEach((el) => {
        el.hidden = !onSale;
        if (onSale) el.textContent = `−${Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)} %`;
      });
      $$('[data-price-wrapper]', this).forEach((el) => {
        el.classList.toggle('is-on-sale', !!onSale);
        el.animate?.([{ opacity: 0.35 }, { opacity: 1 }], { duration: 400 });
      });

      // Boutons
      const label = variant.available ? addBtn?.dataset.labelAdd : addBtn?.dataset.labelSoldout;
      this.setButton(addBtn, variant.available, label);
      if (stickyBtn) stickyBtn.disabled = !variant.available;
      if (payment) payment.hidden = !variant.available;

      // Médias
      if (variant.featured_media && this.gallery) this.gallery.show(variant.featured_media.id);
      const stickyImg = $('.tylps-sticky-atc__img', this);
      if (stickyImg && variant.featured_media?.preview_image?.src) {
        stickyImg.src = `${variant.featured_media.preview_image.src}${variant.featured_media.preview_image.src.includes('?') ? '&' : '?'}width=160`;
        stickyImg.removeAttribute('srcset');
      }
      const stickyVariant = $('[data-sticky-variant]', this);
      if (stickyVariant && this.product.variants.length > 1) stickyVariant.textContent = variant.title;

      // Stock
      this.updateInventory(variant);

      // URL partageable
      if (!T.designMode && !this.hasAttribute('data-no-history')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      document.dispatchEvent(new CustomEvent('tylps:variant:change', { detail: { variant, product: this.product } }));
    }

    setButton(btn, enabled, label) {
      if (!btn) return;
      btn.disabled = !enabled;
      const text = btn.querySelector('.tylps-btn__text');
      const span = btn.querySelector('[data-add-label]');
      if (label) {
        if (span) span.textContent = label;
        if (text) text.dataset.text = label;
      }
    }

    updateInventory(variant) {
      const el = $('[data-inventory]', this);
      if (!el) return;
      const info = this.inventory[variant.id] || {};
      const threshold = parseInt(el.dataset.threshold, 10) || 0;
      let state = 'in';
      let text = el.dataset.in;
      if (!variant.available) {
        state = 'out';
        text = el.dataset.out;
      } else if (info.managed && info.qty > 0 && info.qty <= threshold) {
        state = 'low';
        text = el.dataset.low.replace('[count]', info.qty);
      }
      el.classList.remove('is-in', 'is-low', 'is-out');
      el.classList.add(`is-${state}`);
      const target = el.querySelector('[data-inventory-text]');
      if (target) target.textContent = text;
    }

    /* ---------- Estimation de livraison ---------- */
    initDelivery() {
      const el = $('[data-delivery]', this);
      if (!el) return;
      const cutoff = parseInt(el.dataset.cutoff, 10) || 14;
      const minDays = parseInt(el.dataset.min, 10) || 2;
      const maxDays = Math.max(minDays, parseInt(el.dataset.max, 10) || 4);
      const skipWeekends = el.dataset.skipWeekends === 'true';
      const locale = T.locale || document.documentElement.lang || 'fr';
      const isWorkday = (d) => !skipWeekends || (d.getDay() !== 0 && d.getDay() !== 6);
      const addWorkdays = (date, n) => {
        const d = new Date(date);
        let added = 0;
        while (added < n) {
          d.setDate(d.getDate() + 1);
          if (isWorkday(d)) added++;
        }
        return d;
      };
      const sameDay = (a, b) => a.toDateString() === b.toDateString();
      const fmt = (d) => d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
      const dayWord = (d, now) => {
        if (sameDay(d, now)) return el.dataset.today;
        const tomorrow = new Date(now);
        tomorrow.setDate(now.getDate() + 1);
        if (sameDay(d, tomorrow)) return el.dataset.tomorrow;
        return d.toLocaleDateString(locale, { weekday: 'long' });
      };

      const render = () => {
        const now = new Date();
        let ship = new Date(now);
        const beforeCutoff = isWorkday(now) && now.getHours() < cutoff;
        if (!beforeCutoff) {
          ship = addWorkdays(now, 1);
        }
        const arriveMin = addWorkdays(ship, minDays);
        const arriveMax = addWorkdays(ship, maxDays);
        const textEl = $('[data-delivery-text]', el);
        if (textEl) {
          let text;
          if (beforeCutoff) {
            const end = new Date(now);
            end.setHours(cutoff, 0, 0, 0);
            const mins = Math.max(0, Math.round((end - now) / 60000));
            const h = Math.floor(mins / 60);
            const m = mins % 60;
            const countdown = `<strong class="tylps-delivery__count">${h > 0 ? `${h} h ` : ''}${String(m).padStart(2, '0')} min</strong>`;
            text = textEl.dataset.template.replace('[countdown]', countdown);
          } else {
            text = textEl.dataset.templateLate;
          }
          textEl.innerHTML = text.replace('[ship_day]', `<strong>${dayWord(ship, now)}</strong>`);
        }
        const set = (key, value) => {
          const target = $(`[data-delivery-date="${key}"]`, el);
          if (target) target.textContent = value;
        };
        set('order', fmt(now));
        set('ship', fmt(ship));
        set('arrive', arriveMin.getMonth() === arriveMax.getMonth()
          ? `${arriveMin.getDate()}–${fmt(arriveMax).replace(/^\S+\s/, '')}`
          : `${fmt(arriveMin)} – ${fmt(arriveMax)}`);
        el.classList.add('is-ready');
      };
      render();
      this._deliveryTimer = setInterval(render, 30000);
    }

    /* ---------- Barre d'achat collante ---------- */
    initSticky() {
      const bar = $('[data-sticky-atc]', this);
      const anchor = this.form?.querySelector('[data-add-button]');
      if (!bar || !anchor || !('IntersectionObserver' in window)) return;
      document.body.classList.add('tylps-has-sticky-atc');
      this._stickyObserver = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', passed);
        bar.setAttribute('aria-hidden', String(!passed));
      });
      this._stickyObserver.observe(anchor);
    }
  }
  if (!customElements.get('tylps-product')) customElements.define('tylps-product', TylpsProduct);
})();
/* ==========================================================================
   Formules sac seul / duo : couleurs par sac, prix calculé, ajout de 2 sacs
   ========================================================================== */
(() => {
  'use strict';
  const T = window.TYLPS || {};
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const money = (c) => (T.formatMoney ? T.formatMoney(c) : (c / 100).toFixed(2));
  const imgUrl = (v) => {
    const src = v?.featured_media?.preview_image?.src || v?.featured_image?.src;
    return src ? `${src}${src.includes('?') ? '&' : '?'}width=220` : '';
  };

  function init(root) {
    if (root._bundle) return;
    const scope = root.closest('tylps-product');
    let product;
    try {
      product = JSON.parse(scope.querySelector('[data-product-json]').textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(product?.variants)) return; // relancé à l'événement tylps:product:ready
    root._bundle = true;
    const form = document.getElementById(root.dataset.formId);
    const disc = parseFloat(String(root.dataset.discount).replace(',', '.')) || 0;
    const picker = scope.querySelector('[data-variant-picker]');
    const groups = picker ? $$('[data-option-group]', picker) : [];
    const colorIdx = new Set($$('.tylps-bag__swatches[data-bag-option]', root).map((el) => parseInt(el.dataset.bagOption, 10)));
    const find = (opts) => product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    const mainOptions = () =>
      groups.map((g) => {
        const sel = g.querySelector('select');
        return sel ? sel.value : g.querySelector('input:checked')?.value ?? null;
      });
    const idInput = form?.querySelector('[data-variant-id]');
    const start =
      (groups.length && find(mainOptions())) ||
      product.variants.find((v) => v.id === parseInt(idInput?.value, 10)) ||
      product.variants[0];
    const state = [[...start.options], [...start.options]];
    let tier = parseInt(root.querySelector('.tylps-tier__head input:checked')?.value, 10) || 1;
    let syncing = false;

    const pushMain = () => {
      if (!groups.length) return;
      let changed = null;
      groups.forEach((g, i) => {
        const val = state[0][i];
        const sel = g.querySelector('select');
        if (sel) {
          if (sel.value !== val) {
            sel.value = val;
            changed = sel;
          }
        } else {
          const input = $$('input[type="radio"]', g).find((x) => x.value === val);
          if (input && !input.checked) {
            input.checked = true;
            changed = input;
          }
        }
      });
      if (changed) {
        syncing = true;
        changed.dispatchEvent(new Event('change', { bubbles: true }));
        syncing = false;
      }
    };

    const render = () => {
      const v1 = find(state[0]);
      const v2 = find(state[1]);
      $$('[data-bag]', root).forEach((bag) => {
        const i = parseInt(bag.dataset.bag, 10) - 1;
        const v = i ? v2 : v1;
        $$('[data-bag-option]', bag).forEach((ctrl) => {
          const k = parseInt(ctrl.dataset.bagOption, 10);
          if (ctrl.tagName === 'SELECT') ctrl.value = state[i][k];
          else $$('[data-value]', ctrl).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.value === state[i][k])));
        });
        const img = bag.querySelector('[data-bag-img]');
        const u = imgUrl(v);
        if (img && u && img.dataset.current !== u) {
          img.dataset.current = u;
          img.removeAttribute('srcset');
          img.src = u;
          img.animate?.([{ opacity: 0.2, transform: 'scale(0.94)' }, { opacity: 1, transform: 'none' }], { duration: 320 });
        }
        bag.classList.toggle('is-unavailable', !v || !v.available);
      });
      const p1 = v1?.price || 0;
      const p2 = v2?.price || p1;
      const total = Math.round(((p1 + p2) * (100 - disc)) / 100);
      const t1 = root.querySelector('[data-tier="1"]');
      const t2 = root.querySelector('[data-tier="2"]');
      t1?.querySelector('[data-tier-price]')?.replaceChildren(money(p1));
      if (t2) {
        t2.querySelector('[data-tier-price]')?.replaceChildren(money(total));
        t2.querySelector('[data-tier-compare]')?.replaceChildren(money(p1 + p2));
        const b1 = t2.querySelector('[data-bag="1"]');
        const b2 = t2.querySelector('[data-bag="2"]');
        b1?.querySelector('[data-bag-price]')?.replaceChildren(money(p1));
        b2?.querySelector('[data-bag-price]')?.replaceChildren(money(total - p1));
        b2?.querySelector('[data-bag-compare]')?.replaceChildren(money(p2));
      }

      // Champs envoyés au panier
      const duo = tier === 2;
      if (idInput) idInput.disabled = duo;
      if (form) [...form.elements].filter((q) => q.name === 'quantity' && !q.hasAttribute('data-bundle-qty')).forEach((q) => (q.disabled = duo));
      $$('[data-bundle-input], [data-bundle-qty]', root).forEach((x) => (x.disabled = !duo));
      const i0 = root.querySelector('[data-bundle-input="0"]');
      const i1 = root.querySelector('[data-bundle-input="1"]');
      if (i0) i0.value = v1?.id || '';
      if (i1) i1.value = v2?.id || '';
      form?.querySelector('[data-payment-button]')?.classList.toggle('is-bundle-hidden', duo);
      const btn = form?.querySelector('[data-add-button]');
      if (btn) btn.disabled = duo ? !(v1?.available && v2?.available) : !v1?.available;
    };

    const setTier = (n) => {
      tier = n;
      $$('[data-tier]', root).forEach((t) => {
        const on = parseInt(t.dataset.tier, 10) === n;
        t.classList.toggle('is-selected', on);
        const panel = t.querySelector('.tylps-tier__panel');
        if (panel) panel.hidden = !on;
      });
      if (n === 1) state[1] = [...state[0]];
      render();
    };

    root.addEventListener('change', (e) => {
      const radio = e.target.closest('.tylps-tier__head input[type="radio"]');
      if (radio) return setTier(parseInt(radio.value, 10));
      const sel = e.target.closest('select[data-bag-option]');
      if (!sel) return;
      const i = parseInt(sel.closest('[data-bag]').dataset.bag, 10) - 1;
      state[i][parseInt(sel.dataset.bagOption, 10)] = sel.value;
      if (i === 0) pushMain();
      render();
    });

    root.addEventListener('click', (e) => {
      const sw = e.target.closest('.tylps-bag__swatch');
      if (!sw) return;
      e.preventDefault();
      const i = parseInt(sw.closest('[data-bag]').dataset.bag, 10) - 1;
      const k = parseInt(sw.closest('[data-bag-option]').dataset.bagOption, 10);
      state[i][k] = sw.dataset.value;
      if (i === 0) pushMain();
      else {
        const v = find(state[1]);
        if (v?.featured_media) scope.querySelector('tylps-gallery')?.show?.(v.featured_media.id);
      }
      render();
    });

    // Sélecteur du dessus (ex. Classique / Roller) : la formule suit
    scope.addEventListener('change', (e) => {
      if (syncing || !e.target.closest('[data-variant-picker]')) return;
      const opts = mainOptions();
      opts.forEach((val, k) => {
        if (val == null) return;
        state[0][k] = val;
        if (!colorIdx.has(k)) state[1][k] = val;
      });
      render();
    });

    render();
  }

  const initAll = () => $$('[data-bundle]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('tylps:product:ready', initAll);
})();
